import { Close } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  Stack,
  Typography,
  Box,
  IconButton,
  Button,
  DialogActions,
  DialogContent,
  Grid2,
  TextField,
} from "@mui/material";
import { useTranslation } from "../../../../hooks/useTranslation";
import { ShippingAddress } from "../../types";
import LoadingButton from "@mui/lab/LoadingButton";
import { Controller, useForm } from "react-hook-form";
import { useCustomMutation, useInvalidate } from "@refinedev/core";

type AddressEditModalProps = {
  open: boolean;
  onClose: () => void;
  shippingAddress: ShippingAddress;
  barcode: string;
};

type FieldGroup = {
  side: "left" | "right";
  fields: (keyof ShippingAddress)[];
};

export const fieldGroups: FieldGroup[] = [
  {
    side: "left",
    fields: [
      "name",
      "companyName",
      "addressLine1",
      "addressLine2",
      "addressLine3",
    ],
  },
  {
    side: "right",
    fields: [
      "city",
      "county",
      "district",
      "stateOrRegion",
      "municipality",
      "postalCode",
      "countryCode",
      "phone",
    ],
  },
];

export const AddressEditModal = ({
  open,
  onClose,
  shippingAddress,
  barcode,
}: AddressEditModalProps) => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orderDetails.addressEdit"
  );

  const invalidate = useInvalidate();
  const { mutate, isLoading: isSaveLoading } = useCustomMutation();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: shippingAddress,
  });

  const onSubmit = async (data: ShippingAddress) => {
    const dataToSend = { ...data };

    Object.keys(dataToSend).forEach((key) => {
      if (dataToSend[key as keyof ShippingAddress] === "") {
        delete dataToSend[key as keyof ShippingAddress];
      }
    });

    mutate(
      {
        url: `orders/${barcode}/address`,
        method: "put",
        values: dataToSend,
        successNotification: () => ({
          message: translate("updatedSuccessfully"),
          type: "success",
        }),
      },
      {
        onSuccess: () => {
          onClose();
          invalidate({
            resource: "all",
            invalidates: ["all"],
          });
        },
      }
    );
  };

  const handleClose = () => {
    reset(shippingAddress);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">{translate("title")}</Typography>
          <Box flexGrow={1} />
          <IconButton onClick={handleClose} aria-label="close">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Grid2 container spacing={2}>
          {fieldGroups.map((group) => (
            <Grid2 key={group.side} size={{ xs: 32, md: 6 }}>
              {group.fields.map((field) => (
                <Controller
                  key={field}
                  name={field}
                  control={control}
                  disabled={isSaveLoading}
                  render={({ field: controllerField }) => (
                    <TextField
                      {...controllerField}
                      fullWidth
                      label={translate("addressFields." + field)}
                      margin="dense"
                      size="small"
                    />
                  )}
                />
              ))}
            </Grid2>
          ))}
        </Grid2>
      </DialogContent>

      <DialogActions sx={{ mx: 2, my: 1 }}>
        <Button onClick={handleClose}>
          {translateWithoutPrefix("buttons.cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          loading={isSaveLoading}
          disabled={isSaveLoading || !isDirty}
          onClick={handleSubmit(onSubmit)}
        >
          {translateWithoutPrefix("buttons.save")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
